import { Stack } from '@mantine/core';
import {
  normalizeWaMessage,
  type WaMessageKind,
  type WaMessageView,
} from '../../../lib/whatsapp-message';
import type { InboxMessage } from '../types';
import { ContactsCard } from '../messages/ContactsMessage';
import { FlowCard, FlowMessage } from '../messages/FlowMessage';
import { FlowResponseCard } from '../messages/FlowResponseMessage';
import {
  ButtonReplyMessage,
  InteractiveCard,
  InteractiveMessage,
} from '../messages/InteractiveMessage';
import { LocationCard } from '../messages/LocationMessage';
import { MediaMessage } from '../messages/MediaMessage';
import { OrderCard } from '../messages/OrderMessage';
import { ProductCard } from '../messages/ProductMessage';
import { ReactionMessage } from '../messages/ReactionMessage';
import { StickerMessage } from '../messages/StickerMessage';
import { SystemMessage } from '../messages/SystemMessage';
import { TemplateCard, TemplateMessage } from '../messages/TemplateMessage';
import { TextMessage } from '../messages/TextMessage';
import { UnsupportedMessage } from '../messages/UnsupportedMessage';
import type { WaRenderer, WaRendererProps } from '../messages/primitives';

/**
 * WhatsApp message renderer registry — the channel twin of
 * `messenger-content.tsx`.
 *
 * Adding a message type is: teach `normalizeWaMessage` its payload, then add a
 * row here. No component contains a type ladder, and no component parses raw
 * Meta JSON. Anything the normalizer can't classify resolves to
 * `UnsupportedMessage`, so an unknown or future Meta type degrades to its
 * humanised label instead of vanishing from the thread.
 */
const WA_RENDERERS: Record<WaMessageKind, WaRenderer> = {
  text: { Body: TextMessage },
  media: { Body: MediaMessage },
  sticker: { Body: StickerMessage },
  // `address` carries only a flat field map — the summary is its whole body.
  address: { Body: TextMessage },
  location: { Body: TextMessage, Card: LocationCard },
  contacts: { Body: TextMessage, Card: ContactsCard },
  order: { Body: TextMessage, Card: OrderCard },
  product: { Body: TextMessage, Card: ProductCard },
  interactive: { Body: InteractiveMessage, Card: InteractiveCard },
  button: { Body: ButtonReplyMessage },
  template: { Body: TemplateMessage, Card: TemplateCard },
  flow: { Body: FlowMessage, Card: FlowCard },
  flow_response: { Body: TextMessage, Card: FlowResponseCard },
  reaction: { Body: ReactionMessage },
  system: { Body: SystemMessage },
  unsupported: { Body: UnsupportedMessage },
};

/** The safe fallback every unresolvable kind lands on. */
const FALLBACK: WaRenderer = { Body: UnsupportedMessage };

export function resolveWaRenderer(view: WaMessageView): WaRenderer {
  return WA_RENDERERS[view.kind] ?? FALLBACK;
}

/** The message body, inside the coloured bubble. */
export function WaMessageBody({ view, incoming }: WaRendererProps) {
  const Body = resolveWaRenderer(view).Body ?? TextMessage;
  return <Body view={view} incoming={incoming} />;
}

/** The structured card(s) below the bubble, when the message has any. */
export function WaMessageCard({ view, incoming }: WaRendererProps) {
  const Card = resolveWaRenderer(view).Card;
  if (!Card) return null;
  return <Card view={view} incoming={incoming} />;
}

/**
 * `ChannelCapabilities['renderContent']` seam — the same contract Messenger's
 * registry satisfies, so a future `capabilities/whatsapp.tsx` wiring WhatsApp
 * onto the shared `ChannelInbox` shell is a one-liner.
 */
export function renderWhatsAppContent(m: InboxMessage) {
  const view = normalizeWaMessage({
    isIncoming: m.isIncoming,
    message: m.text,
    messageType: m.messageType,
    payloadJson: m.payloadJson ?? null,
    dataJson: m.dataJson ?? null,
    mediaUrl: m.mediaUrl ?? null,
    mimeType: m.mimeType ?? null,
    reactionEmoji: m.reactionEmoji ?? null,
    wamid: m.reactionTargetId ?? null,
  });
  return (
    <Stack gap={4}>
      <WaMessageBody view={view} incoming={m.isIncoming} />
      <WaMessageCard view={view} incoming={m.isIncoming} />
    </Stack>
  );
}
